import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { IconControl, Loading } from "spark-core-dx/components";
import { useCoreContext } from "spark-core-dx/contexts";
import { useCoreRoute, useGenericManager } from "spark-core-dx/hooks";
import AIPanel from "../../../components/AIChat/AIPanel";
import { AI_PANEL_COMMAND_EVENT } from "../../../components/AIChat/aiPanelCommand";
import { ItemServiceFactory } from "../../../services/ItemServiceFactory";
import "./migrationProjectManage.scss";

const asArray = (value) => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (value.toArray instanceof Function) return value.toArray();
  if (typeof value[Symbol.iterator] === "function") return Array.from(value);
  if (value.first instanceof Function) return [value.first()].filter(Boolean);
  return [];
};

const firstItem = (result) => {
  const normalized = Array.isArray(result) ? result[0] : result;
  const items = asArray(normalized?.Items);
  return items[0] ?? null;
};

const getText = (value) => String(value ?? "").trim();

const getId = (item, ...keys) => {
  for (const key of keys) {
    const value = item?.[key];
    if (value) return String(value);
  }
  return "";
};

const normalizePanelParams = (value) => {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return Object.entries(value).reduce((result, [key, entry]) => {
    const text = getText(entry);
    if (key && text) result[key] = text;
    return result;
  }, {});
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
};

const formatPercent = (value) => {
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "-";
  return `${Math.round(numeric * 100)}%`;
};

const resultMessage = (result) => {
  if (!result) return "";
  if (typeof result.Value === "string") return result.Value;
  if (typeof result.value === "string") return result.value;
  if (result.Value?.message) return result.Value.message;
  if (result.Value?.Message) return result.Value.Message;
  if (result.value?.message) return result.value.message;
  if (result.value?.Message) return result.value.Message;
  return result.Message?.DisplayMessage || result.message?.displayMessage || result.DisplayMessage || result.displayMessage || "";
};

const getDraftId = (draft) => getId(draft, "MappingDraftId", "mappingDraftId", "_id", "UniqueId", "uniqueId");

const getProjectAnalyses = (project) => asArray(project?.Analyses || project?.analyses)
  .filter(Boolean)
  .sort((left, right) => {
    const rightVersion = Number(right?.Version ?? right?.version ?? 0);
    const leftVersion = Number(left?.Version ?? left?.version ?? 0);
    if (rightVersion !== leftVersion) return rightVersion - leftVersion;
    return new Date(right?.GeneratedDate || right?.generatedDate || 0) - new Date(left?.GeneratedDate || left?.generatedDate || 0);
  });

const normalizeSeverity = (value) => {
  const text = String(value || "Info").toLowerCase();
  if (text === "error") return "error";
  if (text === "warning") return "warning";
  return "info";
};

const normalizeStatus = (value) => String(value || "").replace(/([a-z])([A-Z])/g, "$1 $2") || "-";
const DRAFT_CHAT_REFERENCE_NAME = "MigrationMappingDraft";

const sortByMostRecent = (left, right) => {
  const rightTime = new Date(right?.ModifiedDate || right?.modifiedDate || right?.UpdatedDate || right?.updatedDate || right?.CreatedDate || right?.createdDate || 0).getTime();
  const leftTime = new Date(left?.ModifiedDate || left?.modifiedDate || left?.UpdatedDate || left?.updatedDate || left?.CreatedDate || left?.createdDate || 0).getTime();
  if (Number.isFinite(rightTime) && Number.isFinite(leftTime) && rightTime !== leftTime) return rightTime - leftTime;
  return Number(right?.Sequence ?? right?.sequence ?? 0) - Number(left?.Sequence ?? left?.sequence ?? 0);
};

const MigrationMappingDraftManage = ({
  panelMode = false,
  panelOpen = true,
  panelId = null,
  panelParams = null,
  onPanelClose = null,
}) => {
  const { migrationProjectId: routeMigrationProjectId } = useParams();
  const [searchParams] = useSearchParams();
  const resolvedPanelParams = useMemo(() => normalizePanelParams(panelParams), [panelParams]);
  const migrationProjectId = panelMode
    ? (getText(panelId) || getText(searchParams.get("aiPanelId")) || getText(resolvedPanelParams.migrationProjectId) || routeMigrationProjectId)
    : routeMigrationProjectId;
  const coreContext = useCoreContext();
  const profileReady = Boolean(coreContext?.Profile?.ClientId && coreContext?.Profile?.AppId);
  const { coreNavigate } = useCoreRoute();
  const itemServiceFactory = useMemo(() => new ItemServiceFactory(), []);
  const migrationProjectMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.MigrationProject);
  const mappingDraftMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.MigrationMappingDraft);
  const aiChatSessionMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.AIChatSession);
  const managerRef = useRef({ migrationProjectMgr, mappingDraftMgr, aiChatSessionMgr });

  const [project, setProject] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [draftChatSession, setDraftChatSession] = useState(null);
  const [selectedDraftId, setSelectedDraftId] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [status, setStatus] = useState("");
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const loadStateRef = useRef({ key: "", inFlight: false });
  const pendingSelectedDraftIdRef = useRef("");

  const approvedAnalysisId = getId(project, "ApprovedAnalysisId", "approvedAnalysisId");
  const approvedAnalysis = useMemo(() => {
    if (!approvedAnalysisId) return null;
    return getProjectAnalyses(project).find((analysis) => getId(analysis, "AnalysisId", "analysisId", "_id") === approvedAnalysisId) ?? null;
  }, [approvedAnalysisId, project]);

  const draft = useMemo(() => {
    if (selectedDraftId) {
      const selected = drafts.find((item) => getDraftId(item) === selectedDraftId);
      if (selected) return selected;
    }
    return drafts[0] ?? null;
  }, [drafts, selectedDraftId]);

  useEffect(() => {
    managerRef.current = { migrationProjectMgr, mappingDraftMgr, aiChatSessionMgr };
  }, [aiChatSessionMgr, mappingDraftMgr, migrationProjectMgr]);

  const loadData = useCallback(async () => {
    if (panelMode && !panelOpen) return;
    if (!profileReady) {
      setLoading(true);
      return;
    }
    if (!migrationProjectId) {
      setError("Migration project id is required.");
      setLoading(false);
      return;
    }

    const loadKey = `${migrationProjectId}:${reloadKey}`;
    if (loadStateRef.current.inFlight && loadStateRef.current.key === loadKey) return;
    if (!loading && loadStateRef.current.key === loadKey) return;

    loadStateRef.current = { key: loadKey, inFlight: true };
    setLoading(true);
    setError("");
    try {
      const managers = managerRef.current;
      const projectResult = await managers.migrationProjectMgr.getItem(migrationProjectId);
      const nextProject = firstItem(projectResult);
      if (!nextProject) throw new Error("Migration project was not found.");
      setProject(nextProject);

      const draftSessionResult = await managers.aiChatSessionMgr.Query({
        model: "x => x.IsActive == true && x.IsDeleted == false && x.ReferenceId == migrationProjectId && x.ReferenceItemName == referenceItemName",
        params: {
          migrationProjectId,
          referenceItemName: DRAFT_CHAT_REFERENCE_NAME,
        },
      });
      const nextDraftChatSession = asArray(draftSessionResult?.Items)
        .filter(Boolean)
        .sort(sortByMostRecent)[0] ?? null;
      setDraftChatSession(nextDraftChatSession);

      const nextApprovedAnalysisId = getId(nextProject, "ApprovedAnalysisId", "approvedAnalysisId");
      if (!nextApprovedAnalysisId) {
        setDrafts([]);
        setSelectedDraftId("");
        return;
      }

      const draftResult = await managers.mappingDraftMgr.Query({
        model: "x => x.MigrationProjectId == migrationProjectId && x.IsActive == true && x.IsDeleted == false",
        params: { migrationProjectId },
      });
      const nextDrafts = asArray(draftResult?.Items)
        .filter(Boolean)
        .sort((left, right) => {
          const rightVersion = Number(right?.Version ?? right?.version ?? 0);
          const leftVersion = Number(left?.Version ?? left?.version ?? 0);
          if (rightVersion !== leftVersion) return rightVersion - leftVersion;
          return new Date(right?.GeneratedDate || right?.generatedDate || 0) - new Date(left?.GeneratedDate || left?.generatedDate || 0);
        });
      setDrafts(nextDrafts);

      const currentDraftId = getId(nextProject, "CurrentMappingDraftId", "currentMappingDraftId");
      const requestedDraftId = getText(resolvedPanelParams.mappingDraftId || resolvedPanelParams.MappingDraftId);
      const defaultDraftId = requestedDraftId || currentDraftId || getDraftId(nextDrafts[0]);
      if (defaultDraftId) setSelectedDraftId(defaultDraftId);
    } catch (err) {
      setError(err?.message || "Failed to load migration mapping draft.");
    } finally {
      loadStateRef.current = { key: loadKey, inFlight: false };
      setLoading(false);
    }
  }, [loading, migrationProjectId, panelMode, panelOpen, profileReady, reloadKey, resolvedPanelParams.mappingDraftId, resolvedPanelParams.MappingDraftId]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (!drafts.length) {
      if (selectedDraftId) setSelectedDraftId("");
      return;
    }

    const hasSelectedDraft = selectedDraftId && drafts.some((item) => getDraftId(item) === selectedDraftId);
    if (hasSelectedDraft && pendingSelectedDraftIdRef.current === selectedDraftId) {
      pendingSelectedDraftIdRef.current = "";
    }
    if (!hasSelectedDraft && pendingSelectedDraftIdRef.current === selectedDraftId) return;
    if (!hasSelectedDraft) setSelectedDraftId(getDraftId(drafts[0]));
  }, [drafts, selectedDraftId]);

  const handleClose = () => {
    if (panelMode && typeof onPanelClose === "function") {
      onPanelClose();
      return;
    }
    coreNavigate("/dynamic/migrationproject/");
  };

  const handleDraftSessionChange = useCallback((sessionId, sessionItem) => {
    if (!sessionId && !sessionItem) {
      setDraftChatSession(null);
      return;
    }

    setDraftChatSession({
      ...(sessionItem ?? {}),
      _id: sessionId || sessionItem?._id,
      ReferenceId: migrationProjectId,
      ReferenceItemName: DRAFT_CHAT_REFERENCE_NAME,
    });
  }, [migrationProjectId]);

  const handlePanelUpdate = async (uiAction, item, value) => {
    void item;
    const normalizedAction = String(uiAction || "").toLowerCase();
    if (!["migrationmappingdraftupdated", "migrationprojectupdated"].includes(normalizedAction)) return;

    const nextDraftId = getId(value, "mappingDraftId", "MappingDraftId");
    if (nextDraftId) {
      pendingSelectedDraftIdRef.current = nextDraftId;
      setSelectedDraftId(nextDraftId);
    }
    setStatus(value?.message || value?.Message || "Migration mapping draft was updated.");
    setReloadKey((current) => current + 1);
  };

  const openPlanPanel = () => {
    if (typeof window === "undefined") return;
    window.dispatchEvent(new CustomEvent(AI_PANEL_COMMAND_EVENT, {
      detail: {
        panel: "migration",
        route: "/migration/manage/",
        id: migrationProjectId,
        params: { migrationProjectId, analysisId: approvedAnalysisId },
      },
    }));
  };

  const handleGenerateDraft = async () => {
    if (!migrationProjectId || generating || !approvedAnalysisId) return;
    setGenerating(true);
    setError("");
    setStatus("Generating a mapping draft from the approved migration plan.");
    try {
      const result = await migrationProjectMgr.RunOperation({
        operationName: "AIMigrationGenerateMappingDraft",
        id: migrationProjectId,
      });
      setStatus(resultMessage(result) || "Migration mapping draft generated.");

      const returnedDraft = firstItem(result);
      if (returnedDraft) {
        const nextDraftId = getDraftId(returnedDraft);
        pendingSelectedDraftIdRef.current = nextDraftId;
        setSelectedDraftId(nextDraftId);
      } else {
        const nextDraftId = getId(result?.Value || result?.value, "mappingDraftId", "MappingDraftId");
        if (nextDraftId) {
          pendingSelectedDraftIdRef.current = nextDraftId;
          setSelectedDraftId(nextDraftId);
        }
      }
      setReloadKey((current) => current + 1);
    } catch (err) {
      setStatus("");
      setError(err?.message || "Failed to generate migration mapping draft.");
    } finally {
      setGenerating(false);
    }
  };

  const renderDestinationConfig = () => {
    const rows = asArray(draft?.DestinationConfigurationDraft || draft?.destinationConfigurationDraft);
    if (!rows.length) return <div className="migration-project-empty">No destination configuration recommendations are in this draft.</div>;

    return (
      <div className="migration-project-findings">
        {rows.map((item, index) => (
          <div className="migration-project-finding" key={`${item.Title || item.title}-${index}`}>
            <div className="migration-project-finding__header">
              <span className={`migration-project-severity migration-project-severity--${normalizeSeverity(item.Severity || item.severity)}`}>
                {item.Severity || item.severity || "Info"}
              </span>
              <span>{item.Category || item.category || "Destination"}</span>
              {(item.BlocksExecution || item.blocksExecution) ? <span className="migration-draft-blocker">Blocks Execute</span> : null}
            </div>
            <div className="migration-project-finding__title">{item.Title || item.title || "Destination configuration"}</div>
            <div className="migration-project-finding__detail">{item.Detail || item.detail || "-"}</div>
            <div className="migration-project-finding__recommendation">{item.Recommendation || item.recommendation || "-"}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderObjectMappings = () => {
    const rows = asArray(draft?.ObjectMappingDrafts || draft?.objectMappingDrafts);
    if (!rows.length) return <div className="migration-project-empty">No object mappings are in this draft.</div>;

    return (
      <div className="migration-project-table-wrap">
        <table className="migration-project-table migration-project-table--compact">
          <thead>
            <tr>
              <th>Source Object</th>
              <th>Destination Entity</th>
              <th>Confidence</th>
              <th>Status</th>
              <th>Rationale</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item, index) => (
              <tr key={`${item.SourceObjectId || item.sourceObjectId}-${item.EntityObjectId || item.entityObjectId}-${index}`}>
                <td>{item.SourceObjectName || item.sourceObjectName || item.SourceObjectId || item.sourceObjectId}</td>
                <td>{item.EntityObjectName || item.entityObjectName || item.EntityObjectId || item.entityObjectId}</td>
                <td>{formatPercent(item.Confidence ?? item.confidence)}</td>
                <td>{normalizeStatus(item.ReviewStatus || item.reviewStatus)}</td>
                <td>{item.Rationale || item.rationale || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderFieldMappings = () => {
    const rows = asArray(draft?.FieldMappingDrafts || draft?.fieldMappingDrafts);
    if (!rows.length) return <div className="migration-project-empty">No field mappings are in this draft.</div>;

    return (
      <div className="migration-project-table-wrap">
        <table className="migration-project-table">
          <thead>
            <tr>
              <th>Source Field</th>
              <th>Destination Field</th>
              <th>Confidence</th>
              <th>Transform</th>
              <th>Status</th>
              <th>Rationale</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((item, index) => (
              <tr key={`${item.SourceObjectFieldId || item.sourceObjectFieldId}-${item.EntityObjectFieldId || item.entityObjectFieldId}-${index}`}>
                <td>
                  <div>{item.SourceFieldName || item.sourceFieldName || item.SourceObjectFieldId || item.sourceObjectFieldId}</div>
                  <div className="migration-project-muted">{item.SourceObjectName || item.sourceObjectName || ""}</div>
                </td>
                <td>
                  <div>{item.EntityFieldName || item.entityFieldName || item.EntityObjectFieldId || item.entityObjectFieldId}</div>
                  <div className="migration-project-muted">{item.EntityObjectName || item.entityObjectName || ""}</div>
                </td>
                <td>{formatPercent(item.Confidence ?? item.confidence)}</td>
                <td>
                  <div>{normalizeStatus(item.TransformKind || item.transformKind || "None")}</div>
                  <div className="migration-project-muted">{item.TransformHint || item.transformHint || item.ConstantValue || item.constantValue || "-"}</div>
                </td>
                <td>{normalizeStatus(item.ReviewStatus || item.reviewStatus)}</td>
                <td>{item.Rationale || item.rationale || "-"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  const renderValidation = () => {
    const summary = draft?.ValidationSummary || draft?.validationSummary || {};
    const rows = asArray(draft?.ValidationMessages || draft?.validationMessages);

    return (
      <div className="migration-project-stack">
        <div className="migration-project-grid migration-draft-validation-grid">
          <section className="migration-project-panel">
            <div className="migration-project-panel__title">Errors</div>
            <div className="migration-project-stat">{summary.ErrorCount ?? summary.errorCount ?? 0}</div>
          </section>
          <section className="migration-project-panel">
            <div className="migration-project-panel__title">Warnings</div>
            <div className="migration-project-stat">{summary.WarningCount ?? summary.warningCount ?? 0}</div>
          </section>
          <section className="migration-project-panel">
            <div className="migration-project-panel__title">Ready</div>
            <div className="migration-project-stat">{(summary.ReadyForExecute || summary.readyForExecute) ? "Yes" : "No"}</div>
          </section>
          <section className="migration-project-panel">
            <div className="migration-project-panel__title">Stale</div>
            <div className="migration-project-stat">{(summary.IsStale || summary.isStale) ? "Yes" : "No"}</div>
          </section>
        </div>
        {rows.length ? (
          <div className="migration-project-table-wrap">
            <table className="migration-project-table migration-project-table--compact">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Code</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((item, index) => (
                  <tr key={`${item.Code || item.code}-${index}`}>
                    <td>{item.Severity || item.severity || "Info"}</td>
                    <td>{item.Code || item.code || "-"}</td>
                    <td>{item.Message || item.message || "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="migration-project-empty">No validation messages are in this draft.</div>
        )}
      </div>
    );
  };

  const renderDraftContent = () => {
    if (!approvedAnalysisId) {
      return (
        <main className="migration-project-content">
          <div className="migration-project-empty">
            Approve a migration plan before generating a mapping draft.
          </div>
          <div className="migration-project-actions migration-draft-inline-actions">
            <button type="button" onClick={openPlanPanel}>Open Migration Plan</button>
          </div>
        </main>
      );
    }

    return (
      <main className="migration-project-content">
        <div className="migration-project-stack">
          <section className="migration-project-panel migration-project-panel--wide">
            <div className="migration-project-panel__title">Approved Plan</div>
            <p>{approvedAnalysis?.Summary || approvedAnalysis?.summary || "Approved plan snapshot is saved on the migration project."}</p>
            <div className="migration-project-muted">
              v{project?.ApprovedAnalysisVersion || project?.approvedAnalysisVersion || "-"} approved {formatDate(project?.ApprovedDate || project?.approvedDate)}
            </div>
          </section>

          {draft ? (
            <>
              <section className="migration-project-panel migration-project-panel--wide">
                <div className="migration-project-panel__title">Draft Summary</div>
                <p>{draft.Summary || draft.summary || "Mapping draft generated from the approved plan."}</p>
                <div className="migration-project-meta">
                  <span>Status {normalizeStatus(draft.Status || draft.status)}</span>
                  <span>Confidence {formatPercent(draft.Confidence ?? draft.confidence)}</span>
                  <span>Generated {formatDate(draft.GeneratedDate || draft.generatedDate)}</span>
                </div>
              </section>
              <section className="migration-project-section">
                <div className="migration-project-section__title">Destination Configuration</div>
                {renderDestinationConfig()}
              </section>
              <section className="migration-project-section">
                <div className="migration-project-section__title">Object Mappings</div>
                {renderObjectMappings()}
              </section>
              <section className="migration-project-section">
                <div className="migration-project-section__title">Field Mappings</div>
                {renderFieldMappings()}
              </section>
              <section className="migration-project-section">
                <div className="migration-project-section__title">Validation</div>
                {renderValidation()}
              </section>
            </>
          ) : (
            <div className="migration-project-empty">Generate a draft from the approved plan to review executable mapping candidates.</div>
          )}
        </div>
      </main>
    );
  };

  const content = loading ? (
    <div className="migration-project-screen migration-project-screen--loading">
      <Loading />
    </div>
  ) : (
    <div className="migration-project-screen">
      <header className="migration-project-header">
        <div>
          <div className="migration-project-eyebrow">AI Migration Mapping Draft</div>
          <h1>{project?.Name || "Migration Project"}</h1>
          <div className="migration-project-meta">
            <span>{approvedAnalysisId ? "Approved plan selected" : "No approved plan"}</span>
            <span>{draft ? `Draft ${normalizeStatus(draft.Status || draft.status)}` : "No draft"}</span>
          </div>
        </div>
        <div className="migration-project-header-actions">
          {drafts.length > 0 ? (
            <label>
              <span>Draft</span>
              <select value={getDraftId(draft) || ""} onChange={(event) => setSelectedDraftId(event.target.value)}>
                {drafts.map((item) => {
                  const itemId = getDraftId(item);
                  const version = item?.Version ?? item?.version ?? "-";
                  const itemStatus = normalizeStatus(item?.Status || item?.status);
                  const generatedDate = item?.GeneratedDate || item?.generatedDate;
                  return (
                    <option key={itemId} value={itemId}>
                      {`v${version} - ${itemStatus} - ${formatDate(generatedDate)}`}
                    </option>
                  );
                })}
              </select>
            </label>
          ) : null}
          <button
            type="button"
            className="migration-project-run"
            onClick={openPlanPanel}
            title="Open migration plan"
            aria-label="Open migration plan"
          >
            <IconControl iconKey="analytics" width="1rem" height="1rem" />
          </button>
          <button
            type="button"
            className="migration-project-run"
            onClick={handleGenerateDraft}
            disabled={generating || !approvedAnalysisId}
            title={generating ? "Generating mapping draft" : "Generate mapping draft"}
            aria-label={generating ? "Generating mapping draft" : "Generate mapping draft"}
          >
            {generating ? (
              <span className="migration-project-run__spinner" aria-hidden="true" />
            ) : (
              <IconControl iconKey="schema" width="1rem" height="1rem" />
            )}
          </button>
        </div>
      </header>

      {status ? <div className="migration-project-status">{status}</div> : null}
      {error ? <div className="migration-project-warning">{error}</div> : null}
      {renderDraftContent()}
    </div>
  );

  if (!panelMode) return content;

  return (
    <AIPanel
      open={panelOpen}
      title="AI Migration Mapping Draft"
      presentation="slideout"
      mapToClass="core-layout"
      params={{
        ...resolvedPanelParams,
        migrationProjectId,
        approvedAnalysisId,
        mappingDraftId: getDraftId(draft),
        draftPanel: "true",
        referenceItemName: DRAFT_CHAT_REFERENCE_NAME,
      }}
      referenceId={migrationProjectId}
      referenceName={DRAFT_CHAT_REFERENCE_NAME}
      session={draftChatSession}
      operationName="AIQuestionMigration"
      introMessage="Create Mapping Draft..."
      splashMessage="Ask me about this mapping draft."
      singleSessionMode={true}
      planningPreviewEnabled={false}
      panelStorageKey="ai-panel:migration-draft-width"
      onUpdatePanel={handlePanelUpdate}
      onSessionChange={handleDraftSessionChange}
      onCancel={handleClose}
    >
      {content}
    </AIPanel>
  );
};

export default MigrationMappingDraftManage;
